import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  BehaviorSubject,
  filter,
  firstValueFrom,
  Observable,
  switchMap,
} from 'rxjs';
import { TenantConfig } from '../models/TenantConfig.model';
import { SubdomainService } from './subdomain.service';
import { Router } from '@angular/router';
import { ShellConfig, ShellConfigService } from '@adminprodigydesign/axis';
import { BusinessHour } from '../models/business-hour.model';
import { BusinessHoursCalculatorService } from './business-hours-calculator.service';

type LatLng = { lat: number; lng: number };

type TenantFeesPublicResponse = {
  status: 'success' | 'failed';
  message?: string;
  fees?: { serviceFee?: number };
};

type TenantFees = { serviceFee: number };

type BusinessHoursPublicResponse = {
  status: string;
  message: string;
  isOpen?: boolean;
  tenantTz?: string;
  tenantNow?: string;
  businessHour: BusinessHour[];
};

@Injectable({
  providedIn: 'root',
})
export class TenantService {
  // BehaviorSubject to allow components to subscribe to updates
  private tenantConfigSubject = new BehaviorSubject<TenantConfig | null>(null);
  tenantConfig$ = this.tenantConfigSubject.asObservable();

  private tenantUnavailableSubject = new BehaviorSubject<boolean>(false);
  tenantUnavailable$ = this.tenantUnavailableSubject.asObservable();

  private businessHoursSubject = new BehaviorSubject<BusinessHour[] | null>(
    null,
  );
  businessHours$ = this.businessHoursSubject.asObservable();

  private businessHoursMetaSubject = new BehaviorSubject<{
    isOpen?: boolean;
    tenantTz?: string;
    tenantNow?: string;
  } | null>(null);

  businessHoursMeta$ = this.businessHoursMetaSubject.asObservable();

  private routeMatrixClass?: any;

  private tenantFeesSubject = new BehaviorSubject<TenantFees | null>(null);

  tenantFees$ = this.tenantFeesSubject.asObservable();

  constructor(
    private http: HttpClient,
    private subdomainService: SubdomainService,
    public router: Router,
    private shellCfg: ShellConfigService,
    private businessHoursCalc: BusinessHoursCalculatorService,
  ) {}

  private injectShellRouteData(config: TenantConfig) {
    const restaurantName = config.tenant?.companyName || 'Restaurant';

    const address =
      (config as any)?.address?.display ||
      [
        (config as any)?.address?.line1,
        (config as any)?.address?.city,
        (config as any)?.address?.county,
        (config as any)?.address?.postcode,
      ]
        .filter(Boolean)
        .join(', ');

    const headerHours = this.getHeaderDesktopHoursInfo();

    const dynamic: Partial<ShellConfig> = {
      headerType: 'v2',
      companyName: restaurantName,
      navLinks: [
        { label: 'Menu', url: '/menu' },
        { label: 'Account', url: '/account' },
      ],
      headerDesktopInfo: {
        title: restaurantName,
        subtitle: '',
        hoursLabel: headerHours.hoursLabel,
        statusLabel: headerHours.statusLabel,
        statusType: headerHours.statusType,
        address: address || '',
        infoLabel: 'Allergen & Info',
      },
      accountLabel: 'Account',
      accountUrl: '/account',
      showAccountButton: false,
      navAlignment: 'right',
      stacked: false,
      showHeader: true,
      showFooter: true,
      showBurgerIcon: false,
      showCart: true,
      footerData: {
        footerType: 'waitron',
        restaurantName,
        restaurantSubtitle: '',
        poweredByUrl: 'https://waitron.uk',
        poweredByImage: '/images/waitron.svg',
        waitronLinks: [
          { label: 'Menu', url: '/menu' },
          { label: 'Account', url: '/account' },
          { label: 'Cookie Policy', url: '/cookie-policy' },
          { label: 'Terms of Service', url: '/terms-of-service' },
        ],
      },
    };

    this.shellCfg.patch(dynamic);
  }

  loadTenantConfig(): Promise<TenantConfig | null> {
    const isDummy = environment.useDummyTenantData;

    const request$ = isDummy
      ? this.http.get<TenantConfig>(this.tenantPublicUrl)
      : this.http.get<TenantConfig>(this.tenantPublicUrl, {
          params: new HttpParams().set('action', 'viewTenantPublic'),
        });

    return firstValueFrom(request$)
      .then(async (resp) => {
        if ((resp as any)?.status === 'failed') {
          this.tenantUnavailableSubject.next(true);
          if (!this.router.url.includes('/unavailable')) {
            this.router.navigateByUrl('/unavailable');
          }
          return null;
        }

        const baseConfig = resp as TenantConfig;
        if (!baseConfig) throw new Error('Tenant config is undefined');

        this.injectShellRouteData(baseConfig);
        this.tenantConfigSubject.next(baseConfig);

        await Promise.allSettled([
          this.loadTenantTheme(baseConfig),
          this.loadTenantAddress(),
          this.loadBusinessHours(),
          this.loadTenantFeesPublic(),
        ]);

        await this.ensureShopCoords().catch(() => {});

        const finalCfg = this.tenantConfigSubject.value!;
        this.injectShellRouteData(finalCfg);
        this.tenantConfigSubject.next(finalCfg);

        this.tenantUnavailableSubject.next(false);
        if (this.router.url.includes('/unavailable')) {
          this.router.navigateByUrl('/menu');
        }

        return finalCfg;
      })
      .catch(() => {
        this.tenantUnavailableSubject.next(true);
        if (!this.router.url.includes('/unavailable')) {
          this.router.navigateByUrl('/unavailable');
        }
        return null;
      });
  }

  /**
   * Fetch the tenant’s public contact details (viewTenantContactPublic)
   */
  loadTenantAddress(): Promise<void> {
    const isDummy = environment.useDummyTenantData;

    const request$ = isDummy
      ? this.http.get<{
          status: string;
          message: string;
          contact: Array<{
            contactType: string;
            label?: string;
            contactValue: string;
          }>;
        }>(this.tenantContactUrl)
      : this.http.post<{
          status: string;
          message: string;
          contact: Array<{
            contactType: string;
            label?: string;
            contactValue: string;
          }>;
        }>(this.tenantContactUrl, { action: 'viewTenantContactPublic' });

    return firstValueFrom(request$).then((resp) => {
      const contacts = resp?.contact ?? [];

      if (!contacts.length) {
        throw new Error('No tenant contact records returned');
      }

      const addressRec = contacts.find(
        (c) => (c.contactType || '').toLowerCase() === 'address',
      );

      const phoneRec = contacts.find(
        (c) => (c.contactType || '').toLowerCase() === 'phone',
      );

      const patch: any = {};

      if (addressRec?.contactValue) {
        const decoded = this.decodeHtmlEntities(addressRec.contactValue);
        const parsed = this.safeParseJson(decoded);
        patch.address = this.normalizeAddress(parsed);
      }

      if (phoneRec?.contactValue) {
        patch.phone = String(phoneRec.contactValue).trim();
      }

      if (!patch.address && !patch.phone) {
        throw new Error('No usable tenant contact details found');
      }

      this.upsertConfig(patch);
    });
  }

  /**
   * Fetch only the tenant’s theme payload (viewThemePublic)
   * @param themeId  the ID of the theme to load
   */
  loadTenantTheme(baseConfig: TenantConfig): Promise<void> {
    const isDummy = environment.useDummyTenantData;

    const request$ = isDummy
      ? this.http.get<{ status: string; theme: TenantConfig[] }>(
          this.tenantThemeUrl,
        )
      : this.http.post<{ status: string; theme: TenantConfig[] }>(
          this.tenantThemeUrl,
          { action: 'viewThemePublic' },
        );

    return firstValueFrom(request$).then((resp) => {
      const theme = resp?.theme?.[0];
      if (!theme) throw new Error('No active theme returned');

      const current = this.tenantConfigSubject.value ?? baseConfig;
      const merged: TenantConfig = { ...current, ...theme };

      if (!('address' in theme) && (current as any)?.address) {
        (merged as any).address = (current as any).address;
      }

      this.applyTheme(merged);
      this.injectShellRouteData(merged);
      this.tenantConfigSubject.next(merged);
    });
  }

  updateTenant(payload: Partial<TenantConfig>): Observable<TenantConfig> {
    return this.tenantConfig$.pipe(
      filter((config): config is TenantConfig => !!config && !!config._id),
      switchMap((config) =>
        this.http.put<TenantConfig>(`/api/tenants/${config._id}`, payload),
      ),
    );
  }

  private applyTheme(config: any) {
    const root = document.documentElement;
    // Direct mappings from DB
    root.style.setProperty('--color-primary', config.primaryColor || '#CC1B1B');
    root.style.setProperty(
      '--color-primary-soft',
      `color-mix(in srgb, ${config.primaryColor || '#CC1B1B'} 18%, white)`,
    );
    root.style.setProperty(
      '--color-primary-softer',
      `color-mix(in srgb, ${config.primaryColor || '#CC1B1B'} 10%, white)`,
    );
    root.style.setProperty(
      '--color-primary-hover',
      `color-mix(in srgb, ${config.primaryColor || '#CC1B1B'} 8%, white)`,
    );

    root.style.setProperty('--color-text', config.textColor || '#000');
    root.style.setProperty('--nav-links', config.textColor || '#ffffff');
    root.style.setProperty(
      '--color-secondary',
      config.secondaryColor || '#f4f4f4',
    );
    root.style.setProperty(
      '--color-background',
      config.backgroundColor || '#f4f4f4',
    );
    root.style.setProperty(
      '--secondary-text-color',
      config.secondaryTextColor || '#000',
    );
    root.style.setProperty('--color-accent', config.accentColor || '#fe6301');

    // Map backend's `buttonColor` to primary button background
    root.style.setProperty(
      '--button-primary-bg',
      config.buttonColor || config.primaryColor || '#CC1B1B',
    );

    root.style.setProperty(
      '--button-primary-text',
      config.textColor || '#ffffff',
    );
    root.style.setProperty(
      '--button-secondary-bg',
      config.secondaryColor || '#f4f4f4',
    );
    root.style.setProperty(
      '--button-secondary-text',
      config.secondaryTextColor || '#000',
    );
    root.style.setProperty(
      '--color-surface',
      config.secondaryColor || '#fe6301',
    );

    // Optional: fallback status colors
    root.style.setProperty('--danger-color', '#e63946');
    root.style.setProperty('--success-color', '#2a9d8f');
    root.style.setProperty('--warning-color', '#e9c46a');
  }

  /**
   * Fetch the shop’s public business hours.
   */
  loadBusinessHours(): Promise<BusinessHour[]> {
    const isDummy = environment.useDummyTenantData;

    const request$ = isDummy
      ? this.http.get<BusinessHoursPublicResponse>(this.businessHoursUrl)
      : this.http.post<BusinessHoursPublicResponse>(this.businessHoursUrl, {
          action: 'viewBusinessHoursPublic',
        });

    return firstValueFrom(request$).then((resp) => {
      if (resp.status !== 'success' || !resp.businessHour) {
        throw new Error(resp.message || 'Failed to load hours');
      }

      const tenantTz = resp.tenantTz?.replace('\\/', '/') || 'Europe/London';

      this.businessHoursMetaSubject.next({
        isOpen: resp.isOpen,
        tenantTz,
        tenantNow: resp.tenantNow,
      });

      this.businessHoursSubject.next(resp.businessHour);

      const currentCfg = this.tenantConfigSubject.value;
      if (currentCfg) {
        this.injectShellRouteData(currentCfg);
      }

      return resp.businessHour;
    });
  }

  /** Minimal HTML entity decode (no DOM dependency; SSR-safe) */
  private decodeHtmlEntities(s: string): string {
    return s
      .replace(/&quot;/g, '"')
      .replace(/&#34;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&nbsp;/g, ' ');
  }

  /** Try JSON.parse; if it looks double-encoded, parse twice. Fallback to {} */
  private safeParseJson(s: string): any {
    try {
      const once = JSON.parse(s);
      // If result is a string that itself looks like JSON, parse again.
      if (typeof once === 'string' && /^[\[{"]/.test(once.trim())) {
        try {
          return JSON.parse(once);
        } catch {
          /* ignore */
        }
      }
      return once;
    } catch {
      // Sometimes the payload is a raw stringified object with extra quotes or spacing; last-ditch cleanup:
      const cleaned = s.trim().replace(/^\\"|\\\"$/g, '"'); // remove stray escaped quotes at ends
      try {
        return JSON.parse(cleaned);
      } catch {
        return {};
      }
    }
  }

  /** Normalize keys + trim values to your internal address shape */
  private normalizeAddress(input: any) {
    // Accept keys like "streetAddress ", "postalCode", etc., with random spaces/casing
    const map: Record<
      string,
      'line1' | 'city' | 'county' | 'postcode' | 'country' | 'lat' | 'lng'
    > = {
      streetaddress: 'line1',
      address1: 'line1',
      line1: 'line1',
      city: 'city',
      town: 'city',
      county: 'county',
      state: 'county',
      postcode: 'postcode',
      postalcode: 'postcode',
      zip: 'postcode',
      country: 'country',
      lat: 'lat',
      lng: 'lng',
      longitude: 'lng',
      latitude: 'lat',
    };

    const out: any = {};
    const obj = input && typeof input === 'object' ? input : {};

    for (const [rawK, rawV] of Object.entries(obj)) {
      const k = String(rawK).trim().toLowerCase(); // "streetAddress " -> "streetaddress"
      const v = typeof rawV === 'string' ? rawV.trim() : rawV;
      const target = map[k];
      if (target) out[target] = v;
    }

    // Optional: build a nice single-line string for UI
    out.display = [out.line1, out.city, out.county, out.postcode, out.country]
      .filter(Boolean)
      .join(', ');

    return out;
  }

  /** Merge patch into current config and emit (keeps Shell data in sync) */
  private upsertConfig(patch: Partial<TenantConfig>) {
    const current = this.tenantConfigSubject.value ?? ({} as TenantConfig);
    const merged = { ...current, ...patch };
    this.tenantConfigSubject.next(merged);
    this.injectShellRouteData(merged);
  }

  private get shopAddressObject(): any {
    return (this.tenantConfigSubject.value as any)?.address || null;
  }

  get shopAddressDisplay(): string | null {
    const a = this.shopAddressObject;
    if (!a) return null;
    return (
      a.display ||
      [a.line1, a.city, a.county, a.postcode, a.country]
        .filter(Boolean)
        .join(', ') ||
      null
    );
  }

  // 👇 Add this helper inside the class
  private shopCoordsCache?: LatLng;

  /**
   * Returns shop coords, preferring cache or ensureShopCoords() if absent.
   * Throws if it cannot determine coordinates.
   */
  private async getShopLatLng(): Promise<LatLng> {
    if (this.shopCoordsCache) return this.shopCoordsCache;

    // If already present on config.address (ensureShopCoords populated it),
    // use that directly:
    const addr = this.shopAddressObject;
    if (addr?.lat && addr?.lng) {
      this.shopCoordsCache = { lat: Number(addr.lat), lng: Number(addr.lng) };
      return this.shopCoordsCache;
    }

    const ensured = await this.ensureShopCoords();
    if (!ensured) throw new Error('Shop coordinates unavailable.');
    this.shopCoordsCache = ensured;
    return ensured;
  }

  /**
   * Ensure we have shop lat/lng. If not present, geocode the display address once
   * and persist it back into tenantConfig so everyone can reuse it.
   */
  async ensureShopCoords(): Promise<LatLng | null> {
    const addr = this.shopAddressObject;
    if (!addr) return null;

    if (addr.lat && addr.lng) {
      return { lat: Number(addr.lat), lng: Number(addr.lng) };
    }

    // Need to geocode client-side
    const display = this.shopAddressDisplay;
    if (!display) return null;

    // Guard if Google SDK isn't ready
    if (!(window as any).google?.maps?.Geocoder) {
      console.warn('Google Maps Geocoder not available.');
      return null;
    }

    const geocoder = new google.maps.Geocoder();
    const result: google.maps.GeocoderResponse = await geocoder.geocode({
      address: display,
    });

    const loc = result?.results?.[0]?.geometry?.location;
    if (!loc) return null;

    const lat = loc.lat();
    const lng = loc.lng();

    // Save back into config so other pages/components can use it
    const updated = { ...addr, lat, lng };
    this.upsertConfig({ address: updated } as any);

    return { lat, lng };
  }

  async distanceMatrixFromShop(
    customer: LatLng,
  ): Promise<{ miles: number | null; etaText?: string }> {
    if (!Number.isFinite(customer?.lat) || !Number.isFinite(customer?.lng)) {
      return { miles: null };
    }

    const origin = await this.getShopLatLng();

    if (!Number.isFinite(origin?.lat) || !Number.isFinite(origin?.lng)) {
      return { miles: null };
    }

    const key = this.cacheKey(origin, customer);
    const cached = this.distanceCache.get(key);

    if (cached) return cached;

    const g = (window as any).google;

    if (!g?.maps?.importLibrary) {
      console.warn('Google Maps importLibrary not loaded.');
      return { miles: null };
    }

    try {
      const RouteMatrix = await this.getRouteMatrixClass();

      const originLatLng = {
        lat: Number(origin.lat),
        lng: Number(origin.lng),
      };

      const customerLatLng = {
        lat: Number(customer.lat),
        lng: Number(customer.lng),
      };

      const routeMatrixPromise = RouteMatrix.computeRouteMatrix({
        origins: [originLatLng],
        destinations: [customerLatLng],
        travelMode: 'DRIVING',
        routingPreference: 'TRAFFIC_UNAWARE',
        units: g.maps.UnitSystem.IMPERIAL,
        fields: [
          'distanceMeters',
          'durationMillis',
          'condition',
          'localizedValues',
        ],
      });

      const timeout = new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error('RouteMatrix timeout')),
          this.routeMatrixTimeoutMs,
        ),
      );

      const res = await Promise.race([routeMatrixPromise, timeout]);

      const item = res?.matrix?.rows?.[0]?.items?.[0];

      if (!item) {
        return { miles: null };
      }

      const condition = String(item.condition || '').toUpperCase();

      if (condition && condition !== 'ROUTE_EXISTS') {
        console.warn('[RouteMatrix] route condition:', item.condition);
        return { miles: null };
      }

      if (item.distanceMeters != null) {
        const miles = Number(item.distanceMeters) / 1609.344;

        const etaText =
          item.localizedValues?.duration ||
          this.formatDurationMillis(item.durationMillis);

        const out = { miles, etaText };

        this.distanceCache.set(key, out);

        return out;
      }

      return { miles: null };
    } catch (e) {
      console.warn('[RouteMatrix] failed:', e);
      return { miles: null };
    }
  }

  private async getRouteMatrixClass(): Promise<any> {
    if (this.routeMatrixClass) return this.routeMatrixClass;

    const g = (window as any).google;

    if (!g?.maps?.importLibrary) {
      throw new Error('Google Maps importLibrary not loaded.');
    }

    const routesLibrary = await g.maps.importLibrary('routes');

    this.routeMatrixClass =
      routesLibrary.RouteMatrix || g.maps.routes?.RouteMatrix;

    if (!this.routeMatrixClass) {
      throw new Error(
        'RouteMatrix is not available. Make sure the Routes API is enabled.',
      );
    }

    return this.routeMatrixClass;
  }

  private formatDurationMillis(
    durationMillis: number | null | undefined,
  ): string | undefined {
    if (durationMillis == null) return undefined;

    const totalMinutes = Math.max(
      1,
      Math.round(Number(durationMillis) / 60000),
    );

    if (totalMinutes < 60) {
      return `${totalMinutes} min`;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (!minutes) {
      return `${hours} hr`;
    }

    return `${hours} hr ${minutes} min`;
  }

  private routeMatrixTimeoutMs = 4000;

  private distanceCache = new Map<
    string,
    { miles: number; etaText?: string }
  >();

  private cacheKey(a: LatLng, b: LatLng) {
    const r = (n: number) => n.toFixed(4);
    return `${r(a.lat)},${r(a.lng)}|${r(b.lat)},${r(b.lng)}`;
  }

  get serviceFeeDisplay(): string {
    const v = this.serviceFee;
    return `£${v.toFixed(2)}`;
  }

  loadTenantFeesPublic(): Promise<TenantFees> {
    const isDummy = environment.useDummyTenantData;

    const request$ = isDummy
      ? this.http.get<TenantFeesPublicResponse>(this.tenantFeesUrl)
      : this.http.post<TenantFeesPublicResponse>(this.tenantFeesUrl, {
          action: 'viewTenantFeesPublic',
        });

    return firstValueFrom(request$).then((resp) => {
      if (resp?.status !== 'success' || !resp.fees) {
        throw new Error(resp?.message || 'Failed to load tenant fees');
      }

      const fees: TenantFees = {
        serviceFee: Number(resp.fees.serviceFee ?? 0),
      };

      this.tenantFeesSubject.next(fees);
      return fees;
    });
  }

  get serviceFee(): number {
    return this.tenantFeesSubject.value?.serviceFee ?? 0;
  }

  private get tenantPublicUrl(): string {
    return environment.useDummyTenantData
      ? '/dummy/tenant-public.json'
      : environment.tenantConfigUrl;
  }

  private get tenantThemeUrl(): string {
    return environment.useDummyTenantData
      ? '/dummy/tenant-theme.json'
      : environment.tenantConfigUrl;
  }

  private get tenantContactUrl(): string {
    return environment.useDummyTenantData
      ? '/dummy/tenant-contact-public.json'
      : environment.tenantConfigUrl;
  }

  private get businessHoursUrl(): string {
    return environment.useDummyTenantData
      ? '/dummy/business-hours-public.json'
      : environment.tenantConfigUrl;
  }

  private get tenantFeesUrl(): string {
    return environment.useDummyTenantData
      ? '/dummy/tenant-fees-public.json'
      : environment.tenantConfigUrl;
  }

  private getHeaderDesktopHoursInfo(): {
    hoursLabel: string | null;
    statusLabel: string | null;
    statusType: 'open' | 'closed' | 'warning' | null;
  } {
    const allHours = this.businessHoursSubject.value ?? [];

    if (!allHours.length) {
      return {
        hoursLabel: null,
        statusLabel: null,
        statusType: null,
      };
    }

    const meta = this.businessHoursMetaSubject.value;
    const timeZone = this.businessHoursCalc.getTimezone(meta);
    const now = this.businessHoursCalc.getNow(meta);

    const todayStoreHours = this.businessHoursCalc.getTodayBusinessHour(
      allHours,
      'store',
      timeZone,
      now,
    );

    const activeStoreHours = this.businessHoursCalc.getActiveBusinessHourNow(
      allHours,
      'store',
      timeZone,
      now,
    );

    const displayHour = activeStoreHours || todayStoreHours;

    if (!displayHour) {
      return {
        hoursLabel: null,
        statusLabel: null,
        statusType: null,
      };
    }

    const isClosedAllDay = this.businessHoursCalc.isClosedVal(
      displayHour.isClosed,
    );

    const hasTimes = !!displayHour.openTime && !!displayHour.closeTime;

    let hoursLabel: string | null = null;

    if (hasTimes) {
      hoursLabel = `Hours: ${this.businessHoursCalc.formatTime(
        displayHour.openTime,
      )} - ${this.businessHoursCalc.formatTime(displayHour.closeTime)}`;
    } else if (isClosedAllDay) {
      hoursLabel = 'Closed today';
    }

    const isCurrentlyClosed = !activeStoreHours;

    return {
      hoursLabel,
      statusLabel: isCurrentlyClosed ? 'Closed' : 'Open',
      statusType: isCurrentlyClosed ? 'closed' : 'open',
    };
  }

  get shopPhoneDisplay(): string | null {
    return (this.tenantConfigSubject.value as any)?.phone || null;
  }
}
